import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import config from '../../app-config'
import 'react-toastify/dist/ReactToastify.css';
import AdminLayout from '../../layouts/AdminLayout'
import notify from '../../utils/IANotification';
import JoditEditor from 'jodit-react';
import Switch from 'react-switch';
import { HexColorPicker } from "react-colorful";
import FileUploadField from '../../components/FileUploadField';
import SubNavbar from '../../components/ui/SubNavbar';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { MdArrowBack, MdSave, MdDelete } from 'react-icons/md';

export default function SpecialServicesFormPage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  useFetchUserData()

  let special_service_id = new URLSearchParams(window.location.search).get("special_service_id")

  const [activeTab, setActiveTab] = useState("general")
  const [specialServiceData, setSpecialServiceData] = useState({
    name: "",
    description: "",
    tagline: "",
    media_asset_id: "",
    start_datetime: "",
    end_datetime: "",
    is_published: 0,
    theme: "#3B82F6",
  })

  const tabs = [
    { id: 'general', label: 'General Settings' },
    { id: 'schedule_theme', label: 'Schedule & Theme' },
    { id: 'description', label: 'Service Description' },
  ]

  const fetchSpecialServiceById = async () => {
    if (!special_service_id || special_service_id === "new") return
    let endpoint = `${config.endpoint}/services/special/id/${special_service_id}`
    let response = await fetch(endpoint, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
    let data = await response.json()
    if (data?.status === 200) {
      setSpecialServiceData(data.data)
    } else {
      notify({ type: "error", message: data?.message })
    }
  }

  useEffect(() => {
    fetchSpecialServiceById()
  }, [])

  const createSpecialService = async () => {
    let endpoint = `${config.endpoint}/services/special/`
    let response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ ...specialServiceData }),
    })
    let data = await response.json()
    if (data?.status === 200) {
      notify({ type: "success", message: "Special service created successfully" })
      navigate("/admin/special/services/details?special_service_id=" + data.data.id)
    } else {
      notify({ type: "error", message: data?.message })
    }
  }

  const updateSpecialService = async () => {
    if (!special_service_id || special_service_id === "new") return
    let endpoint = `${config.endpoint}/services/special/id/${special_service_id}`
    let response = await fetch(endpoint, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ ...specialServiceData }),
    })
    let data = await response.json()
    if (data?.status === 200) {
      notify({ type: "success", message: "Special service updated successfully" })
    } else {
      notify({ type: "error", message: data?.message })
    }
  }

  const deleteSpecialService = async () => {
    if (!special_service_id || special_service_id === "new") return
    let endpoint = `${config.endpoint}/services/special/id/${special_service_id}`
    let response = await fetch(endpoint, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })
    let data = await response.json()
    if (data?.status === 200) {
      notify({ type: "success", message: "Special service deleted successfully" })
      navigate("/admin/special/services")
    } else {
      notify({ type: "error", message: data?.message })
    }
  }

  const editor = useRef(null);
  const placeholder = "Start typing description...";

  const liveConfig = {
    height: 450,
    placeholder,
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough', 'superscript', 'subscript',
      'ul', 'ol', 'outdent', 'indent', 'font', 'fontsize', 'brush', 'paragraph',
      'table', 'link', 'undo', 'redo', 'eraser', 'copyformat'
    ],
    askBeforePasteHTML: false,
    hotkeys: {
      redo: 'ctrl+z',
      undo: 'ctrl+y,ctrl+shift+z',
      indent: 'ctrl+]',
      outdent: 'ctrl+[',
      bold: 'ctrl+b',
      italic: 'ctrl+i',
    },
  };

  return (
    <AdminLayout
      navSelected={"Special Services"}
      headerText={special_service_id === "new" ? "New Special Service" : "Edit Special Service"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/special/services')}>
              Back
            </Button>
            <h1 className="text-xl font-bold text-gray-900">
              {special_service_id === "new" ? "Create Special Service" : specialServiceData.name || "Edit Special Service"}
            </h1>
          </div>
          <div className="flex gap-2">
            {special_service_id !== "new" && (
              <Button variant="danger" icon={MdDelete} onClick={deleteSpecialService}>
                Delete
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={() => {
              if (special_service_id === "new") createSpecialService()
              else updateSpecialService()
            }}>
              {special_service_id === "new" ? "Create" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      <SubNavbar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* TAB 1: GENERAL SETTINGS */}
      {activeTab === "general" && (
        <Card title="Special Service Profile" subtitle="Configure service title, tagline, header image, and publication status.">
          {special_service_id !== "new" && (
            <div className='mb-6'>
              <FileUploadField
                text={"Upload Special Service Image"}
                upload_url={`${config.endpoint}/file/special/services/id/${special_service_id}`}
                max_file_size={5}
                max_files={1}
                onChange={() => fetchSpecialServiceById()}
              />
            </div>
          )}

          <div className="space-y-6">
            <div className="input-container">
              <label className="input-label font-semibold">Special Service Name</label>
              <input
                type="text"
                className="text-input"
                placeholder='e.g. Christmas Celebration Service 2024'
                value={specialServiceData.name || ""}
                onChange={(e) => setSpecialServiceData({ ...specialServiceData, name: e.target.value })}
              />
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">Tagline</label>
              <input
                type="text"
                placeholder='e.g. Come join us this advent season!'
                className="text-input"
                value={specialServiceData.tagline || ""}
                onChange={(e) => setSpecialServiceData({ ...specialServiceData, tagline: e.target.value })}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div>
                <label className="font-semibold text-sm text-gray-900">Publication Status</label>
                <p className="text-xs text-gray-500">Toggle published state on church portal.</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-gray-700">
                  {specialServiceData.is_published == 1 ? "Published" : "Draft"}
                </span>
                <Switch
                  onChange={(checked) => setSpecialServiceData({ ...specialServiceData, is_published: checked ? 1 : 0 })}
                  checked={specialServiceData.is_published == 1}
                />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* TAB 2: SCHEDULE & THEME */}
      {activeTab === "schedule_theme" && (
        <Card title="Service Schedule &amp; Branding Theme" subtitle="Set service dates and accent theme color.">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="input-container">
                <label className="input-label font-semibold">Start Date &amp; Time</label>
                <input
                  type="datetime-local"
                  className="text-input"
                  value={specialServiceData.start_datetime || ""}
                  onChange={(e) => setSpecialServiceData({ ...specialServiceData, start_datetime: e.target.value })}
                />
              </div>

              <div className="input-container">
                <label className="input-label font-semibold">End Date &amp; Time</label>
                <input
                  type="datetime-local"
                  className="text-input"
                  value={specialServiceData.end_datetime || ""}
                  onChange={(e) => setSpecialServiceData({ ...specialServiceData, end_datetime: e.target.value })}
                />
              </div>
            </div>

            <div className='input-container'>
              <label className="input-label font-semibold">Accent Theme Color</label>
              <div className="flex flex-col sm:flex-row gap-4 items-center pt-2">
                <div
                  className="w-full sm:w-32 h-16 rounded-xl border border-gray-300 shadow-inner"
                  style={{ backgroundColor: specialServiceData?.theme || "#3B82F6" }}
                />
                <HexColorPicker
                  color={specialServiceData?.theme?.toLowerCase() || "#3b82f6"}
                  style={{ width: "240px", height: "120px" }}
                  onChange={(color) => setSpecialServiceData({ ...specialServiceData, theme: color })}
                />
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* TAB 3: DESCRIPTION */}
      {activeTab === "description" && (
        <Card title="Special Service Description" subtitle="Use the rich text editor to compose detailed event information.">
          <div className="space-y-4">
            <JoditEditor
              ref={editor}
              value={specialServiceData.description || ""}
              config={liveConfig}
              tabIndex={1}
              onBlur={newContent => {
                setSpecialServiceData({ ...specialServiceData, description: newContent })
              }}
            />
          </div>
        </Card>
      )}
    </AdminLayout>
  )
}
